import React, { useState, useEffect } from 'react';
import { getSongs, deleteSong } from '../utils/SongsAPIs';
import SongModal from '../components/SongModal'; 
import DeleteConfirmModal from '../components/deleteSongModal'; 

const API_BASE_URL = 
  (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/$/, '');
const MEDIA_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

export default function SongsPage() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Songs Asset');
  const [playingSongId, setPlayingSongId] = useState(null);
  const [audioElement, setAudioElement] = useState(null);

  // Modal States
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [songToEdit, setSongToEdit] = useState(null);
  const [songToDelete, setSongToDelete] = useState(null);
  // 1. Fetch Songs from Backend API
  const fetchSongsList = async () => {
  setLoading(true);
  try {
    const res = await getSongs();
    // Flexible Unwrapping
    let data = res?.data !== undefined ? res.data : res;
    let songList = [];
    if (Array.isArray(data)) {
      songList = data;
    } else if (Array.isArray(data?.songs)) {
      songList = data.songs;
    } else if (Array.isArray(data?.data)) {
      songList = data.data;
    } else if (Array.isArray(data?.result)) {
      songList = data.result;
    } else if (Array.isArray(data?.items)) {
      songList = data.items;
    }
    setSongs(songList);
  } catch (err) {
    console.error('Failed to fetch songs:', err);
  } finally {
    setLoading(false);
  }
 };
 useEffect(() => {
    fetchSongsList();
 }, []);
  // 2. Resolve image & audio URLs
  const getMediaUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${MEDIA_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  // 3. Play / Pause Audio
  const handlePlayPause = (song) => {
    const songId = song._id || song.id;
    const audioUrl = getMediaUrl(song.audioUrl || song.audio || song.songUrl);

    if (!audioUrl) {
      alert('Audio file unavailable');
      return;
    }

    if (playingSongId === songId) {
      audioElement?.pause();
      setPlayingSongId(null);
    } else {
      if (audioElement) audioElement.pause();
      const newAudio = new Audio(audioUrl);
      newAudio.play();
      setAudioElement(newAudio);
      setPlayingSongId(songId);

      newAudio.onended = () => setPlayingSongId(null);
    }
  };

  // 4. Trigger Edit Modal
  const handleEdit = (song) => {
    setSongToEdit(song);
    setIsUploadModalOpen(true);
  };

  // 5. Trigger Delete Confirmation Modal
  const handleDeleteClick = (song) => {
    setSongToDelete(song);
  };

  // 6. Execute Delete after Confirmation
  const handleConfirmDelete = async () => {
    if (!songToDelete) return;
    const idToDelete = songToDelete._id || songToDelete.id;
    
    try {
      await deleteSong(idToDelete);
      if (playingSongId === idToDelete && audioElement) {
        audioElement.pause();
        setPlayingSongId(null);
      }
      setSongToDelete(null);
      fetchSongsList(); // Refresh list after deletion
    } catch (err) {
      console.error('Error deleting song:', err);
    }
  };

  return (
    <div style={{ padding: '2px 0px', backgroundColor: 'transparent', minHeight: '100vh', width: '100%', boxSizing: 'border-box', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      
      {/* Top Header Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#2563eb', margin: '0 0 20px 0' }}>LBK Library</h1>
        </div>

        {/* Upload Button with Inline Plus SVG */}
        <button
          onClick={() => {
            setSongToEdit(null); // Clear edit state for new upload
            setIsUploadModalOpen(true);
          }}
          style={{
            position: 'absolute',
            top: '100px',
            right: '24px',
            backgroundColor: '#2563eb',
            color: '#ffffff',
            padding: '10px 18px',
            borderRadius: '8px',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Upload new songs
        </button>
      </div>

      {/* Navigation Tabs */}
      <div style={{ display: 'flex', gap: '32px', borderBottom: '1px solid #94a3b8', marginBottom: '80px' }}>
        <div
          onClick={() => setActiveTab('Songs Asset')}
          style={{
            paddingBottom: '12px',
            borderBottom: activeTab === 'Songs Asset' ? '3px solid #2563eb' : 'none',
            color: activeTab === 'Songs Asset' ? '#111827' : '#111827',
            fontWeight: activeTab === 'Songs Asset' ? '700' : '500',
            cursor: 'pointer',
            fontSize: '20px'
          }}
        >
          Songs Asset
          <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '400' }}>Manage story songs</div>
        </div>

        <div
          onClick={() => setActiveTab('Emoji')}
          style={{
            paddingBottom: '12px',
            borderBottom: activeTab === 'Emoji' ? '3px solid #2563eb' : 'none',
            color: activeTab === 'Emoji' ? '#111827' : '#111827',
            fontWeight: activeTab === 'Emoji' ? '700' : '500',
            cursor: 'pointer',
            fontSize: '20px'
          }}
        >
          Emoji
          <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '400' }}>Manage chat emoji</div>
        </div>
      </div>

      {/* Main Content View */}
      {activeTab === 'Songs Asset' && (
        <>
          {loading ? (
            <p style={{ color: '#64748b' }}>Loading songs...</p>
          ) : songs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
              <p>No songs uploaded yet. Click <b>"Upload new songs"</b> to add one.</p>
            </div>
          ) : (
            /* Songs Grid */
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 150px))',
              gap: '38px 34px',
              justifyContent: 'center'
            }}>
              {songs.map((song) => {
                const songId = song._id || song.id;
                const coverImage = getMediaUrl(song.coverUrl || song.image || song.cover);
                const isPlaying = playingSongId === songId;

                return (
                  <div key={songId} style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    
                    {/* Square Cover Container */}
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      paddingTop: '100%',
                      borderRadius: '15px',
                      overflow: 'visible',
                      backgroundColor: '#e2e8f0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                      <img
                        src={coverImage || 'https://via.placeholder.com/150'}
                        alt={song.name}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '15px'
                        }}
                      />

                      {/* Top Right Floating Action Icons (Edit & Delete) */}
                      <div style={{
                        position: 'absolute',
                        top: '-7px',
                        right: '-7px',
                        display: 'flex',
                        gap: '6px',
                        zIndex: 10
                      }}>
                        {/* Edit Icon Button */}
                        <button
                          onClick={() => handleEdit(song)}
                          title="Edit Song"
                          style={{
                            width: '19px',
                            height: '19px',
                            borderRadius: '8px',
                            backgroundColor: '#2563eb',
                            color: '#ffffff',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                          </svg>
                        </button>

                        {/* Delete Icon Button */}
                        <button
                          onClick={() => handleDeleteClick(song)}
                          title="Delete Song"
                          style={{
                            width: '19px',
                            height: '19px',
                            borderRadius: '8px',
                            backgroundColor: '#2563eb',
                            color: '#ffffff',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>

                      {/* Play / Pause Clickable Overlay */}
                      <div
                        onClick={() => handlePlayPause(song)}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundColor: isPlaying ? 'rgba(0,0,0,0.3)' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                      >
                        {isPlaying && (
                          <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            color: '#2563eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                              <rect x="6" y="4" width="4" height="16"></rect>
                              <rect x="14" y="4" width="4" height="16"></rect>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Song Titles */}
                    <div style={{ marginTop: '5px', textAlign: 'center' }}>
                      <h4 style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#1e293b',
                        margin: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textAlign: 'center',
                      }}>
                        {song.name || 'Golden Sparrow'}
                      </h4>
                    </div>

                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Upload / Edit Song Modal */}
      {isUploadModalOpen && (
        <SongModal
          isOpen={isUploadModalOpen}
          onClose={() => {
            setIsUploadModalOpen(false);
            setSongToEdit(null);
          }}
          songToEdit={songToEdit}
          onSuccess={fetchSongsList}
        />
      )}

      {/* Delete Confirmation Popup */}
      {songToDelete && (
        <DeleteConfirmModal
          isOpen={!!songToDelete}
          onClose={() => setSongToDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      )}

    </div>
  );
}
